import React from 'react'
import { useTranslation } from 'react-i18next'
import { ReflexContainer, ReflexSplitter, ReflexElement } from 'react-reflex'
import 'react-reflex/styles.css'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Stack from '@mui/material/Stack'
import Checkbox from '@mui/material/Checkbox'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import CircularProgress from '@mui/material/CircularProgress'
import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'

import { Log } from '../../types/log'
import LogChart from '../charts/logChart'
import {
  getCompareSeriesConfig,
  getSeriesColor,
  hexToRGBA,
  getLogEvents,
  getPlotSeriesFromLog,
  getChartMaxTemp,
  getMaxDatapointMsec,
  datapointsToSeries,
  getRorInterval,
} from '../../utils/graphUtils'
import { GraphDatapoint, GraphSeriesConfig, GraphSeriesEvents } from '../../types/graphSeriesConfig'
import { BasicDataParam, GraphDataParam, GraphDataParamString } from '../../types/enums/graphDataParam'
import { get } from '../../utils/service'
import { LegacyDatapoint, Datapoint } from '../../types/datapoint'
import { LogEvent } from '../../types/logEvent'
import Alert from '../alert/alert'
import { getLogName, legacyDatapointsToDatapoints, getEventsFromLogOrDatapoints } from '../../utils/logUtils'
import LogInfoTable from '../logs/logInfoTable'
import { Sensorconfig } from '../../types/sensorconfig'
import { getCompareLogParams, setCompareLogParams } from '../../utils/localStorageUtils'
import { User } from '../../types/user'
import { Machine } from '../../types/machine'

import ViewStepButtons from '../fields/viewStepButtons'
import CompareCheckboxGroup from './compareCheckboxGroup'
import { SeriesType } from '../../types/enums/seriesType'
import { ViewStep } from '../../types/enums/viewStep'

const SPLITTER_WIDTH = 2
const SPLITTER_BORDER_WIDTH = 5

interface CompareViewProps {
  selectedLogs: Array<Log>
  machine: Machine
  user: User | null
  onDeselectLog: (logIdToRemove: number) => void
}

const CompareView = (props: CompareViewProps): JSX.Element => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isScreenBelowMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))

  const fullscreenRef = React.useRef<HTMLElement>(null)

  const [currentStep, setCurrentStep] = React.useState<ViewStep>(ViewStep.Left)
  const [isSelectParamsModalOpen, setIsSelectParamsModalOpen] = React.useState<boolean>(false)
  const [compareParams, setCompareParams] = React.useState<Array<GraphDataParam>>(getCompareLogParams())
  const [isAverageChecked, setIsAverageChecked] = React.useState<boolean>(false)
  const [isShowEventsChecked, setIsShowEventsChecked] = React.useState<boolean>(false)

  const [selectedLogId, setSelectedLogId] = React.useState<number>(0)
  const [resizeCount, setResizeCount] = React.useState<number>(0)

  const [shouldLoadData, setShouldLoadData] = React.useState<boolean>(false)
  const [isFirstLoadDone, setIsFirstLoadDone] = React.useState<boolean>(false)
  const [logsDatapoints, setLogsDatapoints] = React.useState<{ [logUrl: string]: Array<Datapoint> }>({})
  const [logsEvents, setLogsEvents] = React.useState<{ [logUrl: string]: Array<LogEvent> }>({})
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [hasLoadError, setHasLoadError] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (!shouldLoadData) {
      setShouldLoadData(true)
    }
  }, [props.selectedLogs, shouldLoadData])

  React.useEffect(() => {
    if (shouldLoadData && !isFirstLoadDone) {
      setIsLoading(true)
      setIsFirstLoadDone(true)

      Promise.all([
        ...props.selectedLogs.map((log: Log) => get<Array<LegacyDatapoint>>(`datapoints/?page_size=all&log=${log.id}`)),
      ])
        .then((results) => {
          const [...datapointsResponse] = results
          setIsLoading(false)

          const logDatapoints: { [logUrl: string]: Array<Datapoint> } = {}
          const logEvents: { [logUrl: string]: Array<LogEvent> } = {}
          datapointsResponse.forEach((response) => {
            const logUrl = response.data[0].log
            logDatapoints[logUrl] = legacyDatapointsToDatapoints(response.data)
            const log = props.selectedLogs.find((x) => x.url === logUrl) || null
            logEvents[logUrl] = getEventsFromLogOrDatapoints(log, response.data)
          })
          setLogsDatapoints(logDatapoints)
          setLogsEvents(logEvents)
        })
        .catch(() => {
          setIsLoading(false)
          setHasLoadError(true)
        })
    }
  }, [shouldLoadData, isFirstLoadDone, props.selectedLogs])

  const getSensorconfig = (): Sensorconfig | null => {
    return props.machine.sensor_config || null
  }

  const shouldShowStep = (step: ViewStep) => {
    return !isScreenBelowMdBreakpoint || (isScreenBelowMdBreakpoint && currentStep === step)
  }

  // when compare params are changed; update localStorage
  React.useEffect(() => {
    setCompareLogParams(compareParams)
  }, [compareParams])

  const onChangeCompareParam = (logSeriesParam: GraphDataParam): void => {
    setCompareParams((oldCompareParams): Array<GraphDataParam> => {
      return oldCompareParams.indexOf(logSeriesParam) > -1
        ? oldCompareParams.filter((x) => x !== logSeriesParam)
        : [...oldCompareParams, logSeriesParam]
    })
  }

  const calculateSeriesColor = (logParam: GraphDataParam, seriesColor?: string, index?: number): string => {
    const randomAlpha = (60 + Math.random() * 40) / 100
    return getSeriesColor(logParam, theme, index) || hexToRGBA(seriesColor || '#000000', randomAlpha)
  }

  const getSeriesConfig = (logData: Log): Array<GraphSeriesConfig> => {
    const firstLogDatapoints = logsDatapoints ? logsDatapoints[logData.url] : []
    return getPlotSeriesFromLog(firstLogDatapoints || [], getSensorconfig(), theme, null)
  }

  const getFirstSeriesConfig = (): Array<GraphSeriesConfig> => {
    // we need to supply some datapoints, otherwise sensorconfig series will not be shown
    const firstLogDatapoints = props.selectedLogs.length > 0 ? logsDatapoints[props.selectedLogs[0].url] : []
    return getPlotSeriesFromLog(firstLogDatapoints || [], getSensorconfig(), theme, null)
  }

  const calculateCompareSeries = (): Array<GraphSeriesConfig> => {
    const additionalSeries: Array<GraphSeriesConfig> = []
    compareParams.forEach((logParam) => {
      props.selectedLogs.forEach((logData, index) => {
        const seriesConfig = getSeriesConfig(logData).find((x) => x.param === logParam)
        if (seriesConfig) {
          const seriesLabel = `${getLogName(logData, '', t('logview.batch'))} ${t(seriesConfig?.seriesLabel || '')}`
          const seriesColor = calculateSeriesColor(logParam, seriesConfig?.color, index)

          additionalSeries.push({
            logId: logData.id,
            ...getCompareSeriesConfig(
              logParam,
              logData,
              seriesLabel,
              seriesColor,
              seriesConfig?.seriesType || SeriesType.TEMPERATURE,
              logsDatapoints[logData.url],
              props.user
            ),
          })
        }
      })
    })
    return additionalSeries
  }

  const calculateAverageSeries = (): Array<GraphSeriesConfig> => {
    const averageSeries: Array<GraphSeriesConfig> = []
    const allSeries = calculateCompareSeries()
    const maxMsec = getMaxDatapointMsec(allSeries.map((x) => x.datapoints))

    compareParams.forEach((logParam: GraphDataParam) => {
      const averageDatapoints: Array<GraphDatapoint> = []
      const logParamSeries = allSeries.filter((x) => x.param === logParam)

      // loop through all datapoints, calculate average for every msec where dataValue is not null
      for (let msec = 0; msec < maxMsec; msec += 1000) {
        const msecValues: Array<number> = []
        logParamSeries.forEach((x) => {
          const msecDatapoint = x.datapoints.find((y) => y.msec === msec && y.dataValue !== null)
          if (msecDatapoint) {
            msecValues.push(msecDatapoint.dataValue || 0)
          }
        })

        const averageValue = msecValues.reduce((sum: number, current: number) => sum + current, 0) / msecValues.length
        averageDatapoints.push({ msec: msec, dataValue: averageValue })
      }

      averageSeries.push({
        param: logParam,
        seriesId: `avg-${logParam}`,
        seriesLabel: t('logview.averagevalue').replace('{0}', t(GraphDataParamString[logParam])),
        color: calculateSeriesColor(logParam, getFirstSeriesConfig().find((x) => x.param === logParam)?.color),
        seriesType: getFirstSeriesConfig().find((x) => x.param === logParam)?.seriesType || SeriesType.TEMPERATURE,
        datapoints: averageDatapoints,
      })
    })

    return averageSeries
  }

  const calculateLogEvents = (): Array<GraphSeriesEvents> => {
    const logEvents: Array<GraphSeriesEvents> = []
    props.selectedLogs.forEach((logData) => {
      const events = logsEvents[logData.url]
      const btPoints = datapointsToSeries(logsDatapoints[logData.url], BasicDataParam.BT)
      logEvents.push(getLogEvents(events, logData, t('logview.batch'), btPoints, logData.id))
    })

    return logEvents
  }

  const generateCompareParamsCheckboxes = (): JSX.Element => {
    return (
      <FormGroup row sx={{ justifyContent: 'space-between' }}>
        <CompareCheckboxGroup
          seriesConfig={getFirstSeriesConfig()}
          checkedCompareParams={compareParams}
          onChange={(param: GraphDataParam): void => onChangeCompareParam(param)}
          row={true}
        />
        <Stack key="average" direction="row" sx={{ marginLeft: 'auto' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAverageChecked}
                onChange={() => setIsAverageChecked((oldAverageValue) => !oldAverageValue)}
                name="average"
              />
            }
            label={t('logview.average')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isShowEventsChecked}
                onChange={() => setIsShowEventsChecked((oldShowEventsValue) => !oldShowEventsValue)}
                name="showevents"
              />
            }
            label={t('logview.showevents')}
          />
        </Stack>
      </FormGroup>
    )
  }

  return (
    <>
      <Stack sx={{ height: '100%', width: '100%', flex: 1 }}>
        {isLoading && <CircularProgress />}
        {hasLoadError && (
          <Stack alignSelf="flex-start">
            <Alert severity="error">{t('logview.comparedatapointserror')}</Alert>
          </Stack>
        )}
        {isSelectParamsModalOpen && (
          <Dialog open={isSelectParamsModalOpen} onClose={() => setIsSelectParamsModalOpen(false)}>
            <DialogContent>{generateCompareParamsCheckboxes()}</DialogContent>
            <DialogActions>
              <Button onClick={() => setIsSelectParamsModalOpen(false)}>{t('common.close')}</Button>
            </DialogActions>
          </Dialog>
        )}
        {Object.keys(logsDatapoints).length > 0 && (
          <>
            <ReflexContainer orientation="vertical">
              {shouldShowStep(ViewStep.Left) && (
                <ReflexElement flex={1} minSize={200}>
                  <LogInfoTable
                    logs={props.selectedLogs}
                    logDatapoints={logsDatapoints}
                    machine={props.machine}
                    selectedLogId={selectedLogId}
                    onSelectedLog={setSelectedLogId}
                    onDeselectLog={props.onDeselectLog}
                  />
                </ReflexElement>
              )}
              {shouldShowStep(ViewStep.Left) && shouldShowStep(ViewStep.Center) && (
                <ReflexSplitter
                  style={{
                    width: SPLITTER_WIDTH + 2 * SPLITTER_BORDER_WIDTH,
                    borderLeft: `${SPLITTER_BORDER_WIDTH}px solid ${theme.palette.background.default}`,
                    borderRight: `${SPLITTER_BORDER_WIDTH}px solid ${theme.palette.background.default}`,
                    backgroundColor: theme.palette.secondary.main,
                    marginRight: 16,
                    marginLeft: 16,
                  }}
                />
              )}
              {shouldShowStep(ViewStep.Center) && (
                <ReflexElement flex={2} minSize={300} onStopResize={() => setResizeCount((oldCount) => oldCount + 1)}>
                  <Stack height="100%" justifyContent="flex-end" ref={fullscreenRef}>
                    <LogChart
                      chartMaxTemp={getChartMaxTemp(props.user, props.machine)}
                      exportTitle={t('logview.comparelogs')}
                      seriesConfig={calculateCompareSeries()}
                      eventDatapoints={isShowEventsChecked ? calculateLogEvents() : []}
                      expectedSeries={[]}
                      compareLogSeries={[]}
                      averageSeries={isAverageChecked ? calculateAverageSeries() : undefined}
                      compareFocusLogId={selectedLogId}
                      resizeCount={resizeCount}
                      rorInterval={getRorInterval(props.user)}
                      onRequestFullscreen={() => {
                        if (fullscreenRef.current) {
                          fullscreenRef.current.requestFullscreen()
                        }
                      }}
                    />
                    {compareParams.length === 0 && <Alert severity="info">{t('logsview.selectparamsinfo')}</Alert>}
                    {!isScreenBelowMdBreakpoint && generateCompareParamsCheckboxes()}
                  </Stack>
                </ReflexElement>
              )}
            </ReflexContainer>
          </>
        )}
        {isScreenBelowMdBreakpoint && (
          <Stack sx={{ position: 'fixed', bottom: 0, right: 0, left: 0, backgroundColor: 'background.default' }}>
            <ViewStepButtons
              steps={[ViewStep.Left, ViewStep.Center]}
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              children={
                shouldShowStep(ViewStep.Center) ? (
                  <Button sx={{ zIndex: 1, alignSelf: 'center' }} onClick={() => setIsSelectParamsModalOpen(true)}>
                    {t('logsview.selectparams')}
                  </Button>
                ) : undefined
              }
            />
          </Stack>
        )}
      </Stack>
    </>
  )
}

export default CompareView
